import { getPricingsByAgentId, patchSetRangesForAgent, Pricing } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

export const MAX_ALLOWED_PERIOD = 144;
export const MIN_ALLOWED_PERIOD = 1;

export const MAX_ALLOWED_PRINCIPAL = 500000;
export const MIN_ALLOWED_PRINCIPAL = 0;

interface UseEditAgentPricingsProps {
  agentPricings: Pricing[];
  agentId: string;
}

export interface PricingRanges {
  principalMin: number;
  principalMax: number;
  periodMin: number;
  periodMax: number;
}

export const useEditAgentPricings = ({ agentPricings, agentId }: UseEditAgentPricingsProps) => {
  const { request } = useRequest();
  const { revalidate } = useCache(getPricingsByAgentId, { agentId });
  const [pricingRanges, setPricingRanges] = useState<PricingRanges>({
    principalMin: 0,
    principalMax: 0,
    periodMin: 0,
    periodMax: 0,
  });

  const { principalMax, principalMin, periodMax, periodMin } = pricingRanges;

  const [loading, setLoading] = useState<boolean>(false);

  const getRangesForPricings = {
    getMaxRanges(array: Pricing[], key: 'rangePeriodMax' | 'rangePrincipalMax') {
      return array.reduce((max, obj) => (max && (obj[key] ?? 0) > max ? obj[key] : max), agentPricings[0][key]) ?? 0;
    },
    getMinRanges(array: Pricing[], key: 'rangePeriodMin' | 'rangePrincipalMin') {
      return array.reduce((min, obj) => (min && (obj[key] ?? 0) < min ? obj[key] : min), agentPricings[0][key]) ?? 0;
    },
  };

  useEffect(() => {
    const principalMax = getRangesForPricings.getMaxRanges(agentPricings, 'rangePrincipalMax');
    const periodMax = getRangesForPricings.getMaxRanges(agentPricings, 'rangePeriodMax');
    const principalMin = getRangesForPricings.getMinRanges(agentPricings, 'rangePrincipalMin');
    const periodMin = getRangesForPricings.getMinRanges(agentPricings, 'rangePeriodMin');
    setPricingRanges({
      principalMin,
      principalMax,
      periodMin,
      periodMax,
    });
  }, [agentPricings]);

  const handleEditAgentPricings = async () => {
    setLoading(true);
    try {
      await request(patchSetRangesForAgent, {
        id: agentId,
        rangePeriodMin: Math.round(periodMin),
        rangePeriodMax: Math.round(periodMax),
        rangePrincipalMin: principalMin,
        rangePrincipalMax: principalMax,
      });
      await revalidate();
    } catch (error) {
      throw new Error(`Unable to edit prices ranges: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const minValueGreaterThanMax = (minValue: number, maxValue: number) => minValue >= maxValue;

  const minPeriodGreaterThanMaxPeriod = minValueGreaterThanMax(periodMin, periodMax);
  const minPrincipalGreaterThanMaxPrincipal = minValueGreaterThanMax(principalMin, principalMax);

  return {
    loading,
    handleEditAgentPricings,
    minPeriodGreaterThanMaxPeriod,
    minPrincipalGreaterThanMaxPrincipal,
    pricingRanges,
    setPricingRanges,
  };
};
